// contacts-diff — PURE, DOM-free helpers for computing what changed between two
// snapshots of the owner's flat contact list (one row per subscription). Used by
// the contacts view to surface a "N new / N updated" summary banner after a
// poll-driven refetch or an explicit "Sync all". Unit-tested; no imports.

export interface ContactLite {
  subscription_id: string;
  // Stable per-PERSON id (owner_id). Multiple subscriptions to the same person
  // share one owner_id; when absent we fall back to name (then subscription_id).
  owner_id?: string;
  name: string;
  content_changed_at?: string | null;
}

export interface ContactsDiff {
  addedNames: string[];
  updatedNames: string[];
  addedCount: number;
  updatedCount: number;
}

interface PersonAgg {
  key: string;
  name: string;
  // Max content_changed_at across this person's subscriptions (change marker).
  changed: string;
}

// personKey groups by PERSON (owner_id preferred) so multiple subscriptions to
// one person count as ONE contact — mirrors mergeContactsByOwner semantics.
function personKey(c: ContactLite): string {
  return c.owner_id || c.name || c.subscription_id;
}

// aggregate collapses the flat list into one entry per person, keeping the
// human display name and the MAX content_changed_at (the change marker).
function aggregate(list: ContactLite[]): Map<string, PersonAgg> {
  const m = new Map<string, PersonAgg>();
  for (const c of list) {
    const key = personKey(c);
    const changed = c.content_changed_at || '';
    const existing = m.get(key);
    if (!existing) {
      m.set(key, { key, name: c.name || '', changed });
    } else {
      // Prefer a non-empty name; keep the max change marker.
      if (!existing.name && c.name) existing.name = c.name;
      if (changed > existing.changed) existing.changed = changed;
    }
  }
  return m;
}

// diffContacts computes people ADDED (present in next, absent in prev) and
// UPDATED (present in both, content_changed_at advanced). A person is never
// counted as both added and updated. Names are the per-person display names.
export function diffContacts(prev: ContactLite[], next: ContactLite[]): ContactsDiff {
  const prevAgg = aggregate(prev);
  const nextAgg = aggregate(next);

  const addedNames: string[] = [];
  const updatedNames: string[] = [];

  for (const [key, cur] of nextAgg) {
    const old = prevAgg.get(key);
    if (!old) {
      addedNames.push(cur.name);
      continue;
    }
    // Updated iff the change marker advanced (strictly greater ISO string).
    if (cur.changed && cur.changed > old.changed) {
      updatedNames.push(cur.name);
    }
  }

  return {
    addedNames,
    updatedNames,
    addedCount: addedNames.length,
    updatedCount: updatedNames.length,
  };
}
